import { getConfig } from "@/config";
import { getUser } from "@/auth/userManager";

/** The error shape every Kontora service returns (architecture-guide.md §4.10). */
export interface ApiViolation {
  field: string;
  /**
   * What was wrong, in a form a program can act on (K-36). The client renders
   * the sentence from this; `message` is the English one the API wrote, shown
   * when the code is not one this build knows.
   */
  code: string;
  message: string;
  params?: Record<string, string>;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    violations?: ApiViolation[];
  };
}

/**
 * An error carrying the API's status, code and per-field violations.
 *
 * Violations are preserved as structured data rather than flattened into a
 * string, so a form can show each message next to the input it belongs to.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly violations: ApiViolation[];

  constructor(status: number, body: ApiErrorBody | undefined, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.error?.code ?? "unknown";
    this.violations = body?.error?.violations ?? [];
  }

  /** True when the caller must re-authenticate. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Returns the violation for a field, if the API rejected it. */
  violationFor(field: string): ApiViolation | undefined {
    return this.violations.find((v) => v.field === field);
  }
}

/** The global list envelope every collection endpoint returns (guide §4.11). */
export interface Page<T> {
  data: T[];
  pagination: {
    limit: number;
    has_more: boolean;
    next_cursor: string | null;
  };
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * The bearer header for the current access token, or none.
 *
 * Every function in this file that talks to the API needs exactly this, read
 * fresh per request rather than captured once (a silent renew is picked up
 * without rebuilding anything). Pulled out so `apiFetch`, `apiDownload` and
 * `apiFetchText` attach the token the same way rather than three copies of the
 * same two lines drifting apart.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const user = await getUser();
  return user?.access_token ? { Authorization: `Bearer ${user.access_token}` } : {};
}

/** Turns a failed response into the `ApiError` every caller expects. */
async function throwForStatus(response: Response): Promise<never> {
  let body: ApiErrorBody | undefined;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // A non-JSON error body (a proxy 502, say) is not exceptional; fall back
    // to the status text rather than masking the real failure.
  }
  throw new ApiError(response.status, body, response.statusText);
}

/**
 * Calls the API with the current access token attached.
 *
 * The token is read per request rather than captured once, so a silent renew is
 * picked up without rebuilding the client.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { apiBaseUrl } = getConfig();

  const headers: Record<string, string> = { Accept: "application/json", ...(await authHeaders()) };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const init: RequestInit = {
    method: options.method ?? "GET",
    headers,
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }
  if (options.signal) {
    init.signal = options.signal;
  }

  const response = await fetch(`${apiBaseUrl}${path}`, init);

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    return throwForStatus(response);
  }

  return (await response.json()) as T;
}

/**
 * Fetches a file the API serves as plain text (the manual's Markdown, ADR 0008).
 *
 * Not `apiFetch`: the body is Markdown, not JSON, and parsing it as JSON would
 * throw on every chapter. Same token, same base URL, same error mapping.
 */
export async function apiFetchText(
  path: string,
  options: { signal?: AbortSignal } = {},
): Promise<string> {
  const { apiBaseUrl } = getConfig();
  const init: RequestInit = { headers: await authHeaders() };
  if (options.signal) {
    init.signal = options.signal;
  }

  const response = await fetch(`${apiBaseUrl}${path}`, init);
  if (!response.ok) {
    return throwForStatus(response);
  }
  return response.text();
}

/** A downloaded file: its bytes and the name the server gave it. */
export interface Download {
  blob: Blob;
  filename: string;
}

/**
 * Fetches a file the API serves as an attachment.
 *
 * A plain `<a href>` cannot do this: the token is a bearer header, not a cookie,
 * so a link would arrive unauthenticated and the browser would render a 401.
 * The file is fetched with the same credentials every other call uses and handed
 * back as a blob for the caller to save.
 *
 * The filename comes from Content-Disposition rather than being guessed here.
 * The server already decides it — it knows the range that was exported — and two
 * places deciding it is one place to disagree.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<Download> {
  const { apiBaseUrl } = getConfig();
  const response = await fetch(`${apiBaseUrl}${path}`, { headers: await authHeaders() });

  if (!response.ok) {
    return throwForStatus(response);
  }

  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get("Content-Disposition")) ?? fallbackName,
  };
}

/** Reads the filename out of a Content-Disposition header. */
export function filenameFrom(disposition: string | null): string | undefined {
  if (!disposition) return undefined;
  const quoted = /filename="([^"]+)"/.exec(disposition);
  if (quoted?.[1]) return quoted[1];
  const bare = /filename=([^;]+)/.exec(disposition);
  return bare?.[1]?.trim();
}
